import { useCallback, useEffect } from "react";
import { Input, SelectOption, Select, Textarea, Form, Row, Flex, Span, Avatar, Button, Icon, Icons, DatePicker } from "@ir/ui";
import { Deduction } from "@/core/entities/deduction";
import EditDeductionDetailsCol from "./EditDeductionDetailsCol";
import { useDeductionEdit } from "@/presentation/deduction/hooks/deduction";
import { useWorkspace } from "@/hooks";
import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { IUpdateDeductionDetailsRepositoryParams } from "@/core/repositories/deduction";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { DeductionEditMode } from "@/store/deduction/deduction";
import { DeductionInformationLabels } from "../utils/deductionInformationLabel.enum";
import { useDeductionDetailsOptions } from "./hooks/useDeductionDetailsOptions";

interface EditDeductionDetailsProps {
  deduction: Deduction;
  onCancel?: () => void;
}

const toSelectOption = (label?: string | null, value?: string | number | null) => {
  if (!label || value === null || value === undefined) {
    return null;
  }
  return {
    label,
    value: value.toString(),
  };
};

const buildDeductionFormValues = (deduction: Deduction) => ({
  deductionType: toSelectOption(deduction.deductionType, deduction.deductionTypeId),
  deductionSubType: toSelectOption(deduction.deductionSubType, deduction.deductionSubTypeId),
  glCode: toSelectOption(deduction.glCode, deduction.glCodeId),
  totalAmount: deduction.totalAmount,
  deductedAmount: deduction.amount,
  purchaser: deduction.purchaser,
  distributionCenter: deduction.distributionCenterCode
    ? {
      label: `${deduction.distributionCenter} (${deduction.distributionCenterCode})`,
      value: deduction.distributionCenterCode,
    }
    : null,
  deductionDate: deduction.deductionDate,
  poNumber: deduction.poNumber,
  period: deduction.period,
  description: deduction.description ?? "",
  analyst: toSelectOption(deduction.analystUserFullName, deduction.analystUserId),
  approver: toSelectOption(deduction.approverUserFullName, deduction.approverUserId),
  retailer: toSelectOption(deduction.retailerName, deduction.retailerKey),
});

const EditDeductionDetails = ({ deduction, onCancel }: EditDeductionDetailsProps) => {
  const [EditDeductionDetailsForm] = Form.useForm();
  const { setDeductionDetails, patchDeductionDetails, isDeductionDetailsUpdating, editMode, setEditMode } = useDeductionEdit();

  const { setHasAnyUnsavedChanges } = useDeductionDrawer();

  const { settings } = useWorkspace();

  const {
    analystOptions,
    approverOptions,
    deductionTypeOptions,
    deductionSubTypeOptions,
    distributionCenterOptions,
    glCodeOptions,
    retailerOptions,
    handleDeductionTypeMetaChange,
    handleDeductionSubTypeMetaChange,
    syncDeductionSubTypeOptions,
    brandMembers,
    isBrandMembersLoading,
    isDeductionTypesLoading,
    isDistributionCentersLoading,
    isGlCodesLoading,
    isRetailersLoading,
  } = useDeductionDetailsOptions({ deduction, form: EditDeductionDetailsForm });

  useEffect(() => {
    if (!deduction) {
      return;
    }
    EditDeductionDetailsForm.setFieldsValue(buildDeductionFormValues(deduction));
    syncDeductionSubTypeOptions(deduction.deductionTypeId?.toString());
  }, [deduction, EditDeductionDetailsForm, syncDeductionSubTypeOptions]);

  const handleCancel = useCallback(
    ({ preserveEditMode }: { preserveEditMode?: boolean } = {}) => {
      setHasAnyUnsavedChanges(false);
      onCancel?.();
      EditDeductionDetailsForm.resetFields();
      if (!preserveEditMode) {
        setEditMode(null);
      }
    },
    [EditDeductionDetailsForm, onCancel, setEditMode, setHasAnyUnsavedChanges]
  );

  useEffect(() => {
    if (editMode === DeductionEditMode.INTELLIGENCE) {
      handleCancel({ preserveEditMode: true });
    }
  }, [editMode, handleCancel]);

  const renderUserOption = (option: SelectOption) => {
    const brandMember = brandMembers?.find(eachBrandMember => eachBrandMember.userId.toString() === option.value);

    const isUnknownUser: boolean = !brandMember && (option.value != null || option.value != undefined || option.value != "");
    const isDeleted: boolean = (isUnknownUser || brandMember?.isDeleted) ?? false;

    return isBrandMembersLoading ? (
      <Flex align="center" justifyContent="start" direction="row" gap={0.5} wrap="no-wrap" basis="max-content">
        <Avatar shape="circle" size="extra-small" name="" title="" />
        <Span className="font-medium flex-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {""}
        </Span>
      </Flex>
    ) : (
      <Flex align="center" justifyContent="start" direction="row" gap={0.5} wrap="no-wrap" basis="max-content">
        <Avatar
          shape="circle"
          size="extra-small"
          name={brandMember?.fullName ?? ""}
          src={brandMember?.avatar ?? ""}
          isDeleteUser={isDeleted}
          title={isDeleted ? "Removed User" : (brandMember?.fullName ?? "Unassigned")}
        />
        <Span className="font-medium flex-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isUnknownUser ? "Unknown" : (brandMember?.fullName ?? "")}
        </Span>
      </Flex>
    );
  };

  const dateService = new DateService(new DateAdapter());

  const today = dateService.getCurrentDate().toISOString();
  const todayFormatted = dateService.format(today, "YYYY-MM-DD");

  const deductionDateMaxValue = todayFormatted;

  const handleSubmit = async () => {
    const validateFields = await EditDeductionDetailsForm.validateFields({
      dirty: true,
    });

    const deductionDetails: IUpdateDeductionDetailsRepositoryParams = {
      deduction_id: deduction.id,
      deduction_type_id: validateFields.deductionSubType?.value ? Number.parseInt(validateFields.deductionSubType?.value) : null,
      gl_code_id: validateFields.glCode?.value ? Number.parseInt(validateFields.glCode?.value) : null,
      distribution_center_code: validateFields.distributionCenter?.value,
      po_number: validateFields.poNumber && validateFields.poNumber.length > 0 ? validateFields.poNumber : null,
      deduction_date: validateFields.deductionDate,
      document_total_amount: validateFields?.totalAmount ? Number.parseFloat(validateFields?.totalAmount) : null,
      period: validateFields.period && validateFields.period.length > 0 ? validateFields.period : null,
      analyst_user_id: validateFields.analyst?.value ? Number.parseInt(validateFields.analyst?.value) : null,
      approver_user_id: validateFields.approver?.value ? Number.parseInt(validateFields.approver?.value) : null,
      deduction_description: validateFields.description && validateFields.description.length > 0 ? validateFields.description : null,
      retailer_key: validateFields.retailer?.value ? validateFields.retailer?.value : null,
    };

    setDeductionDetails(deductionDetails);

    const updatedDeduction = await patchDeductionDetails();

    if (updatedDeduction) {
      setHasAnyUnsavedChanges(false);
      onCancel?.();
      setEditMode(null);
    }
  };

  const handleValuesChange = () => {
    setHasAnyUnsavedChanges(true);
  };

  return (
    <Form form={EditDeductionDetailsForm} className="editDeductionDetails" onFinish={handleSubmit} onValuesChange={handleValuesChange}>
      <Row className="flex-1">
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item
            name="deductionType"
            isList
            label={DeductionInformationLabels.DeductionType}
            rules={[{ required: true, message: `${DeductionInformationLabels.DeductionType} is Required` }]}
            onMetaChange={handleDeductionTypeMetaChange}
          >
            <Select size="small" options={deductionTypeOptions} isClearable={false} isLoading={isDeductionTypesLoading} />
          </Form.Item>
        </EditDeductionDetailsCol>

        <EditDeductionDetailsCol className="mb-3">
          <Form.Item
            name="deductionSubType"
            isList
            label={DeductionInformationLabels.DeductionSubType}
            rules={[{ required: true, message: `${DeductionInformationLabels.DeductionSubType} is Required` }]}
            onMetaChange={handleDeductionSubTypeMetaChange}
          >
            <Select size="small" options={deductionSubTypeOptions} isClearable={false} isLoading={isDeductionTypesLoading} />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item
            name="glCode"
            isList
            label={DeductionInformationLabels.GLCode}
            rules={[{ required: true, message: `${DeductionInformationLabels.GLCode} is Required` }]}
          >
            <Select size="small" options={glCodeOptions} isClearable={false} isLoading={isGlCodesLoading} />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="deductedAmount" label={DeductionInformationLabels.DeductedAmount}>
            <Input size="small" isReadOnly isDisabled type="number" className="deducted-amount-input" />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="purchaser" label={DeductionInformationLabels.Purchaser}>
            <Input size="small" isReadOnly isDisabled />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item
            name="distributionCenter"
            isList
            label={DeductionInformationLabels.DC}
            rules={[{ required: true, message: `${DeductionInformationLabels.DC} is Required` }]}
          >
            <Select size="small" options={distributionCenterOptions} isClearable={false} isLoading={isDistributionCentersLoading} />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3 deduction_date_picker">
          <Form.Item
            name="deductionDate"
            label={DeductionInformationLabels.DeductionDate}
            rules={[{ required: true, message: `${DeductionInformationLabels.DeductionDate} is Required` }]}
          >
            <DatePicker
              dateFormat={settings?.displayDateFormat}
              maxValue={deductionDateMaxValue || todayFormatted}
              shouldCloseOnInteractOutside={true}
              isNonModal={true}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item
            name="totalAmount"
            label={DeductionInformationLabels.TotalAmount}
            rules={[{ required: deduction.totalAmount != null, message: `${DeductionInformationLabels.TotalAmount} is Required` }]}
          >
            <Input size="small" type="number" className="deducted-amount-input" />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="period" label={DeductionInformationLabels.Period}>
            <Input size="small" maxLength={50} allowClear />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="poNumber" label={DeductionInformationLabels.PONumber}>
            <Input size="small" maxLength={50} allowClear />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="analyst" isList label={DeductionInformationLabels.Analyst}>
            <Select
              size="small"
              placeholder=""
              options={analystOptions}
              customOptionLabel={option => renderUserOption(option)}
              isClearable={true}
              isLoading={isBrandMembersLoading}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="approver" isList label={DeductionInformationLabels.Approver}>
            <Select
              size="small"
              placeholder=""
              options={approverOptions}
              customOptionLabel={option => renderUserOption(option)}
              isClearable={true}
              isLoading={isBrandMembersLoading}

            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="retailer" isList label={DeductionInformationLabels.Retailer}>
            <Select size="small" placeholder="" options={retailerOptions} isClearable={false} isLoading={isRetailersLoading} />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol className="mb-3">
          <Form.Item name="reasonCode" label={DeductionInformationLabels.ReasonCode}>
            <Input size="small" maxLength={50} isReadOnly isDisabled defaultValue={deduction?.reasonCode} />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol md={12} className="mb-3">
          <Form.Item name="description" label={DeductionInformationLabels.Description}>
            <Textarea size="small" maxLength={500} showCharCount />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol md={12} className="mb-0">
          <Form.Item>
            <Flex justifyContent="end" className="custom-button-container">
              <Button size="medium" color="grey" variant="clear" onPress={handleCancel} isDisabled={isDeductionDetailsUpdating}>
                <Icon name={Icons.Close} slot="start" size="medium" />
                Cancel
              </Button>

              <Button size="medium" variant="solid" type="submit" isDisabled={isDeductionDetailsUpdating}>
                <Icon
                  name={isDeductionDetailsUpdating ? Icons.Loading : Icons.Check}
                  slot="start"
                  size="medium"
                  className={isDeductionDetailsUpdating ? "icon-fast-loading" : ""}
                />
                Save
              </Button>
            </Flex>
          </Form.Item>
        </EditDeductionDetailsCol>
      </Row>
    </Form>
  );
};

export default EditDeductionDetails;
